import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Users, Plus, Upload, Search, Trash2, Database, ChevronRight, X } from 'lucide-react';
import { crmV2API } from '../../services/api';
import { Card, Badge, PageTitle, PrimaryButton, SecondaryButton } from './ui';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';
import { timeAgo } from './api';

/* =========================================================
   Customers — CRM v2. Customer groups (imported record sets)
   and their records. Wired to /api/crm-v2 (agent server 3016).
   ========================================================= */

const NewGroupModal = ({ open, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  if (!open) return null;

  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setErr('');
    try {
      const res = await crmV2API.createGroup({ groupName: name.trim(), fieldNames: [] });
      onCreated?.(res.data.group || res.data);
      setName('');
      onClose();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <div className="w-full max-w-[420px] rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-semibold">New Customer Group</h3>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>
        <label className="mt-4 block text-[12.5px] font-semibold text-slate-600">Group name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Q1 Leads"
          autoFocus
          onKeyDown={(e) => e.key === 'Enter' && create()}
          className="mt-1.5 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
        />
        {err && <p className="mt-2 text-[12.5px] text-red-600">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={create} disabled={saving || !name.trim()}>
            {saving ? 'Creating…' : 'Create Group'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const ImportModal = ({ open, onClose, groups, onImported }) => {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState('new'); // 'new' | 'existing'
  const [newName, setNewName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  if (!open) return null;

  const reset = () => {
    setFile(null); setMode('new'); setNewName(''); setGroupId(''); setErr('');
  };
  const close = () => { reset(); onClose(); };

  // One-step import — prepare and commit in a single action (no preview step).
  const doImport = async () => {
    if (!file) return setErr('Choose a file to import.');
    if (mode === 'new' && !newName.trim()) return setErr('Enter a name for the new group.');
    if (mode === 'existing' && !groupId) return setErr('Select a group to import into.');
    setBusy(true); setErr('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('targetType', mode);
      if (mode === 'new') fd.append('newGroupName', newName.trim());
      else fd.append('groupId', groupId);
      const prev = await crmV2API.previewImport(fd);
      const sessionId = prev.data?.importSessionId;
      if (!sessionId) throw new Error('Import could not be prepared.');
      const res = await crmV2API.commitImport(sessionId);
      onImported?.({
        result: res.data,
        groupId: mode === 'existing' ? groupId : res.data?.groupId || res.data?.group?.id,
        groupName: mode === 'new' ? newName.trim() : undefined,
      });
      close();
    } catch (e) {
      setErr(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={close}>
      <div className="w-full max-w-[480px] rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-[16px] font-semibold">Import Customers (CSV / Excel)</h3>
          <button onClick={close} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100">
            <X size={18} />
          </button>
        </div>

        <label className="mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-center hover:border-teal-300 hover:bg-teal-50/40">
          <Upload size={22} className="text-teal-600" />
          <span className="text-[13px] font-semibold">{file ? file.name : 'Choose a .csv, .xls, or .xlsx file'}</span>
          <span className="text-[12px] text-slate-400">First row becomes the field names.</span>
          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            hidden
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <div className="mt-4 flex gap-4 text-[13px] font-medium">
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} className="accent-teal-600" />
            New group
          </label>
          <label className="flex items-center gap-1.5">
            <input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} className="accent-teal-600" />
            Existing group
          </label>
        </div>
        {mode === 'new' ? (
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New group name"
            className="mt-2 w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
          />
        ) : (
          <select
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] outline-none focus:border-teal-500"
          >
            <option value="">Select a group…</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>{g.groupName}</option>
            ))}
          </select>
        )}
        {err && <p className="mt-2 text-[12.5px] text-red-600">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <SecondaryButton onClick={close}>Cancel</SecondaryButton>
          <PrimaryButton onClick={doImport} disabled={busy || !file}>
            {busy ? 'Importing…' : 'Import records'}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

const DemoCustomers = () => {
  const [groups, setGroups] = useState(null);
  const [error, setError] = useState(null);
  const [groupQuery, setGroupQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);

  const [records, setRecords] = useState(null);
  const [recordQuery, setRecordQuery] = useState('');
  const [recordsError, setRecordsError] = useState(null);

  const [newGroupOpen, setNewGroupOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const loadGroups = useCallback(async () => {
    try {
      const r = await crmV2API.listGroups();
      const gs = r.data.groups || [];
      setGroups(gs);
      return gs;
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      return [];
    }
  }, []);
  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const selected = useMemo(() => (groups || []).find((g) => g.id === selectedId) || null, [groups, selectedId]);

  const loadRecords = useCallback((id) => {
    if (!id) {
      setRecords(null);
      return;
    }
    setRecords(null);
    setRecordsError(null);
    crmV2API
      .listRecords(id, {})
      .then((r) => setRecords(r.data.records || []))
      .catch((e) => setRecordsError(e.response?.data?.message || e.message));
  }, []);

  // Load records when a group is selected
  useEffect(() => {
    loadRecords(selectedId);
  }, [selectedId, loadRecords]);

  // After an import, refresh groups, open the target group, and reload its records.
  const handleImported = useCallback(
    async (info) => {
      const gs = await loadGroups();
      let target = null;
      if (info?.groupId) target = gs.find((g) => String(g.id) === String(info.groupId));
      if (!target && info?.groupName) target = gs.find((g) => g.groupName === info.groupName);
      if (target) {
        setSelectedId(target.id);
        loadRecords(target.id); // force refresh even if it was already selected
      }
    },
    [loadGroups, loadRecords]
  );

  const filteredGroups = (groups || []).filter((g) =>
    g.groupName.toLowerCase().includes(groupQuery.trim().toLowerCase())
  );

  const filteredRecords = useMemo(() => {
    if (!records) return [];
    const q = recordQuery.trim().toLowerCase();
    if (!q) return records;
    return records.filter((rec) =>
      Object.values(rec.fields || {}).some((v) => String(v ?? '').toLowerCase().includes(q))
    );
  }, [records, recordQuery]);

  const deleteGroup = async (g) => {
    if (!window.confirm(`Delete group "${g.groupName}" and all its records? This cannot be undone.`)) return;
    try {
      await crmV2API.deleteGroup(g.id);
      if (selectedId === g.id) setSelectedId(null);
      setGroups((gs) => (gs || []).filter((x) => x.id !== g.id));
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.message || e.message}`);
    }
  };

  const columns = selected?.fieldNames || [];

  return (
    <div className="flex h-full flex-col">
      <NewGroupModal
        open={newGroupOpen}
        onClose={() => setNewGroupOpen(false)}
        onCreated={() => loadGroups()}
      />
      <ImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        groups={groups || []}
        onImported={handleImported}
      />

      <PageTitle
        title="Customers"
        subtitle="Manage your customer groups and the contact records your AI can use."
        action={
          <div className="flex items-center gap-2.5">
            <SecondaryButton onClick={() => setImportOpen(true)}>
              <Upload size={16} /> Import
            </SecondaryButton>
            <PrimaryButton onClick={() => setNewGroupOpen(true)}>
              <Plus size={16} /> New Group
            </PrimaryButton>
          </div>
        }
      />

      <div className="mt-6 grid min-h-0 flex-1 auto-rows-fr grid-cols-1 gap-5 overflow-hidden xl:grid-cols-[340px_1fr]">
        {/* Groups */}
        <Card className="flex h-full min-h-0 flex-col overflow-hidden p-4">
          <div className="flex shrink-0 items-center gap-2.5 rounded-xl border border-slate-200 px-3.5 py-2.5">
            <Search size={16} className="text-slate-400" />
            <input
              value={groupQuery}
              onChange={(e) => setGroupQuery(e.target.value)}
              placeholder="Search groups…"
              className="w-full bg-transparent text-[14px] outline-none"
            />
          </div>

          <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
            {error && <ErrorNote error={error} />}
            {!error && groups === null && <LoadingRows label="Loading groups…" />}
            {!error && groups !== null && filteredGroups.length === 0 && (
              <p className="py-10 text-center text-[13px] text-slate-400">
                {groups.length === 0 ? 'No customer groups yet — create or import one.' : 'No groups match your search.'}
              </p>
            )}
            <div className="space-y-1">
              {filteredGroups.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelectedId(g.id)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors ${
                    selectedId === g.id ? 'border-teal-500 bg-teal-50/40' : 'border-transparent hover:bg-slate-50'
                  }`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600">
                    <Users size={17} strokeWidth={1.9} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13.5px] font-semibold">{g.groupName}</span>
                    <span className="block text-[12px] text-slate-500">
                      {Number(g.recordCount || 0).toLocaleString()} records · updated {timeAgo(g.updatedAt || g.createdAt)}
                    </span>
                  </span>
                  <ChevronRight size={15} className="shrink-0 text-slate-300" />
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Records */}
        <Card className="flex h-full min-h-0 flex-col overflow-hidden p-6">
          {!selected ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Database size={28} strokeWidth={1.7} />
              </span>
              <p className="mt-4 text-[15px] font-semibold">Select a customer group</p>
              <p className="mt-1.5 max-w-xs text-[13px] text-slate-500">
                Choose a group on the left to view and manage its contact records.
              </p>
            </div>
          ) : (
            <>
              <div className="flex shrink-0 items-start justify-between gap-4">
                <div>
                  <h2 className="text-[17px] font-semibold">{selected.groupName}</h2>
                  <p className="mt-0.5 text-[12.5px] text-slate-500">
                    {Number(selected.recordCount || 0).toLocaleString()} records · {columns.length} fields
                  </p>
                </div>
                <button
                  onClick={() => deleteGroup(selected)}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-[12.5px] font-semibold text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 size={13} /> Delete group
                </button>
              </div>

              <div className="mt-4 flex shrink-0 items-center gap-2.5 rounded-xl border border-slate-200 px-3.5 py-2.5">
                <Search size={16} className="text-slate-400" />
                <input
                  value={recordQuery}
                  onChange={(e) => setRecordQuery(e.target.value)}
                  placeholder="Search records…"
                  className="w-full bg-transparent text-[14px] outline-none"
                />
              </div>

              <div className="mt-4 min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200">
                {recordsError && <div className="p-4"><ErrorNote error={recordsError} /></div>}
                {!recordsError && records === null && <div className="p-4"><LoadingRows label="Loading records…" /></div>}
                {!recordsError && records !== null && columns.length === 0 && (
                  <p className="py-12 text-center text-[13px] text-slate-400">
                    This group has no fields yet. Import a CSV to add records.
                  </p>
                )}
                {!recordsError && records !== null && columns.length > 0 && (
                  <table className="w-full text-left">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-[12px] text-slate-500">
                        {columns.map((c) => (
                          <th key={c} className="whitespace-nowrap px-4 py-2.5 font-semibold">{c}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50 text-[13px]">
                      {filteredRecords.length === 0 && (
                        <tr>
                          <td colSpan={columns.length} className="px-4 py-10 text-center text-[13px] text-slate-400">
                            {records.length === 0 ? 'No records in this group.' : 'No records match your search.'}
                          </td>
                        </tr>
                      )}
                      {filteredRecords.map((rec) => (
                        <tr key={rec.id} className="hover:bg-slate-50/60">
                          {columns.map((c) => (
                            <td key={c} className="whitespace-nowrap px-4 py-2.5 text-slate-700">
                              {String(rec.fields?.[c] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {records !== null && (
                <p className="mt-2 shrink-0 text-[11.5px] text-slate-400">
                  Showing {filteredRecords.length.toLocaleString()} of {records.length.toLocaleString()} records
                </p>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DemoCustomers;
